// modal tworzenia procesu

import React, { FC, useMemo, useEffect, useState } from 'react';
import { useFormik } from 'formik';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';
import classnames from 'classnames';

import { Modal, Input, FormElement, Button, ErrorMessage, Select } from 'components/controls';
import { useGetProcessTypes } from 'api/endpoints/useGetProcessTypes';
import { useGetModules, IModule } from 'api/endpoints/useGetModules';
import { useGetDomainEvents } from 'api/endpoints/useGetDomainEvents';
import { useGetPolicyKinds } from 'api/endpoints/useGetPolicyKinds';
import { useGetPolicySubtypes } from 'api/endpoints/useGetPolicySubtypes';
import { useGetProviderKinds } from 'api/endpoints/useGetProviderKinds';
import { useGetAdditionalFieldsServices } from 'api/endpoints/useGetAdditionalFieldsServices';
import { useGetProductLabelFields } from 'api/endpoints/useGetProductLabelFields';
import { useGetLayoutLanguages } from 'api/endpoints/useGetLayoutLanguages';
import { useGetProcessEntityClasses } from 'api/endpoints/useGetProcessEntityClasses';
import { Options } from 'react-select';

import styles from 'theme/components/containers/CreateProcessModal/CreateProcessModal.module.scss';

interface ICreateProcessModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: { name: string; description: string; type: string; module_id: number; additional_data?: { event_class?: string; policy_kind_tag?: string; policy_subtype_class?: string; additional_fields_service_interface?: string; product_label_field?: string; translations?: Record<string, string>; provider_kind_tag?: string; entity_class?: string } }) => void;
}

const CreateProcessModal: FC<ICreateProcessModalProps> = ({
  open,
  onClose,
  onSubmit
}) => {
  const { t } = useTranslation();
  const { data: processTypesData, isLoading: isLoadingTypes } = useGetProcessTypes();
  const { data: modulesData, isLoading: isLoadingModules } = useGetModules();
  const { data: domainEventsData, isLoading: isLoadingDomainEvents } = useGetDomainEvents();
  const { data: policyKindsData, isLoading: isLoadingPolicyKinds } = useGetPolicyKinds();
  const [selectedPolicyKind, setSelectedPolicyKind] = useState<string | null>(null);
  const { data: policySubtypesData, isLoading: isLoadingPolicySubtypes } = useGetPolicySubtypes(selectedPolicyKind);
  const { data: additionalFieldsServicesData, isLoading: isLoadingAdditionalFieldsServices } = useGetAdditionalFieldsServices();
  const { data: productLabelFieldsData, isLoading: isLoadingProductLabelFields } = useGetProductLabelFields();
  const { data: languagesData } = useGetLayoutLanguages();
  const { data: providerKindsData, isLoading: isLoadingProviderKinds } = useGetProviderKinds();
  const { data: entityClassesData, isLoading: isLoadingEntityClasses } = useGetProcessEntityClasses();

  // Opcje klas encji dla validatora
  const entityClassOptions = useMemo(() => {
    const devStudio = (entityClassesData?.developmentStudio ?? []).map(item => ({
      value: item.value,
      label: item.text,
      item
    }));
    const system = (entityClassesData?.system ?? []).map(item => ({
      value: item.value,
      label: item.text,
      item
    }));
    return [...devStudio, ...system];
  }, [entityClassesData]);

  // Przygotowanie opcji dla Select - typy procesów
  const typeOptions = useMemo(() => {
    if (!processTypesData?.items) return [];
    return processTypesData.items.map(type => ({
      value: type.value,
      label: type.text,
      item: type
    }));
  }, [processTypesData]);

  // Przygotowanie opcji dla Select - moduły
  const moduleOptions: Options<{ value: string; label: string; item: IModule }> = useMemo(() => {
    const items = Array.isArray(modulesData) ? modulesData : (modulesData as any)?.items;
    if (!items) return [];
    return items.map((module: IModule) => ({
      value: module.id.toString(),
      label: module.name,
      item: module
    }));
  }, [modulesData]);

  // Domyślna wartość - pierwszy typ z listy
  const defaultType = useMemo(() => {
    return typeOptions.length > 0 ? typeOptions[0].value : '';
  }, [typeOptions]);

  // Domyślna wartość - pierwszy moduł z listy
  const defaultModuleId = useMemo(() => {
    return moduleOptions.length > 0 ? parseInt(moduleOptions[0].value, 10) : null;
  }, [moduleOptions]);

  // Opcje rodzajów polityk dla Policy
  const policyKindOptions = useMemo(() => {
    if (!policyKindsData?.items) return [];
    return policyKindsData.items.map(kind => ({
      value: kind.value,
      label: kind.text,
      item: kind
    }));
  }, [policyKindsData]);

  // Opcje podtypów polityk
  const policySubtypeOptions = useMemo(() => {
    if (!policySubtypesData?.items) return [];
    return policySubtypesData.items.map(subtype => ({
      value: subtype.value,
      label: subtype.text,
      item: subtype
    }));
  }, [policySubtypesData]);

  // Opcje serwisów pól dodatkowych
  const additionalFieldsServiceOptions = useMemo(() => {
    if (!additionalFieldsServicesData?.items) return [];
    return additionalFieldsServicesData.items.map(service => ({
      value: service.value,
      label: service.text,
      item: service
    }));
  }, [additionalFieldsServicesData]);

  // Opcje pól dodatkowych dla product_label
  const productLabelFieldOptions = useMemo(() => {
    if (!productLabelFieldsData?.items) return [];
    return productLabelFieldsData.items.map(field => ({
      value: field.value,
      label: field.text,
      item: field
    }));
  }, [productLabelFieldsData]);

  // Opcje rodzajów providerów
  const providerKindOptions = useMemo(() => {
    if (!providerKindsData?.items) return [];
    return providerKindsData.items.map(kind => ({
      value: kind.value,
      label: kind.text,
      item: kind
    }));
  }, [providerKindsData]);

  // Opcje eventów domenowych dla Listenera
  const domainEventOptions = useMemo(() => {
    if (!domainEventsData?.items) return [];
    return domainEventsData.items.map(event => ({
      value: event.value,
      label: event.text,
      item: event
    }));
  }, [domainEventsData]);

  const { values, errors, setFieldValue, handleSubmit, resetForm } = useFormik({
    initialValues: {
      name: '',
      description: '',
      type: '',
      module_id: null as number | null,
      event_class: '',
      policy_kind: '',
      policy_subtype_class: '',
      additional_fields_service: '',
      product_label_field: '',
      translations: {} as Record<string, string>,
      provider_kind: '',
      entity_class: ''
    },
    enableReinitialize: true,
    validationSchema: Yup.object().shape({
      name: Yup.string().required(t('Pole wymagane')),
      description: Yup.string(),
      type: Yup.string().required(t('Pole wymagane')),
      module_id: Yup.number().required(t('Pole wymagane')).nullable(),
      event_class: Yup.string().when('type', {
        is: 'listener',
        then: (schema) => schema.required(t('Pole wymagane')),
        otherwise: (schema) => schema
      }),
      policy_kind: Yup.string().when('type', {
        is: 'policy',
        then: (schema) => schema.required(t('Pole wymagane')),
        otherwise: (schema) => schema
      }),
      additional_fields_service: Yup.string().when('type', {
        is: 'additional_fields',
        then: (schema) => schema.required(t('Pole wymagane')),
        otherwise: (schema) => schema
      }),
      product_label_field: Yup.string().when('type', {
        is: 'product_label',
        then: (schema) => schema.required(t('Pole wymagane')),
        otherwise: (schema) => schema
      }),
      provider_kind: Yup.string().when('type', {
        is: 'provider',
        then: (schema) => schema.required(t('Pole wymagane')),
        otherwise: (schema) => schema
      }),
      entity_class: Yup.string().when('type', {
        is: 'validator',
        then: (schema) => schema.required(t('Pole wymagane')),
        otherwise: (schema) => schema
      })
    }),
    onSubmit: (formValues) => {
      if (!formValues.module_id) {
        return;
      }
      const additionalData: { event_class?: string; policy_kind_tag?: string; policy_subtype_class?: string; additional_fields_service_interface?: string; product_label_field?: string; translations?: Record<string, string>; provider_kind_tag?: string; entity_class?: string } = {};
      if (formValues.type === 'listener' && formValues.event_class) additionalData.event_class = formValues.event_class;
      if (formValues.type === 'policy' && formValues.policy_kind) additionalData.policy_kind_tag = formValues.policy_kind;
      if (formValues.type === 'policy' && formValues.policy_subtype_class) additionalData.policy_subtype_class = formValues.policy_subtype_class;
      if (formValues.type === 'additional_fields' && formValues.additional_fields_service) additionalData.additional_fields_service_interface = formValues.additional_fields_service;
      if (formValues.type === 'product_label' && formValues.product_label_field) additionalData.product_label_field = formValues.product_label_field;
      if (formValues.type === 'provider' && formValues.provider_kind) additionalData.provider_kind_tag = formValues.provider_kind;
      if (formValues.type === 'validator' && formValues.entity_class) additionalData.entity_class = formValues.entity_class;
      if (formValues.type === 'product_label') {
        const nonEmpty = Object.fromEntries(
          Object.entries(formValues.translations).filter(([, v]) => v !== '')
        );
        if (Object.keys(nonEmpty).length > 0) {
          additionalData.translations = nonEmpty;
        }
      }
      onSubmit({
        name: formValues.name,
        description: formValues.description,
        type: formValues.type,
        module_id: formValues.module_id,
        ...(Object.keys(additionalData).length > 0 && { additional_data: additionalData })
      });
      resetForm();
      onClose();
    },
    validateOnChange: false
  });

  const handleClose = () => {
    resetForm();
    onClose();
  };

  // Ustaw domyślną wartość gdy dane się załadują
  useEffect(() => {
    if (defaultType && !values.type && typeOptions.length > 0) {
      setFieldValue('type', defaultType);
    }
  }, [defaultType, typeOptions.length, values.type, setFieldValue]);

  return (
    <Modal open={open} onClose={handleClose} title={t('Utwórz nowy proces')}>
      <div
        className={classnames(
          styles.formContent,
          'StylePath-Components-Containers-CreateProcessModal'
        )}>
        <FormElement>
          <label>{t('Moduł')} *</label>
          <Select<IModule>
            value={values.module_id?.toString() || ''}
            selectedOption={moduleOptions.find(opt => opt.value === values.module_id?.toString()) || null}
            onChange={(selected) => {
              setFieldValue('module_id', selected ? selected.id : null);
            }}
            options={moduleOptions}
            placeholder={isLoadingModules ? t('Ładowanie...') : t('Wybierz moduł')}
            disabled={isLoadingModules}
            error={errors.module_id}
          />
          {errors.module_id && <ErrorMessage>{errors.module_id}</ErrorMessage>}
        </FormElement>

        <FormElement>
          <label>{t('Typ procesu')} *</label>
          <Select
            value={values.type}
            selectedOption={typeOptions.find(opt => opt.value === values.type) || null}
            onChange={(selected) => {
              setFieldValue('type', selected?.value || '');
            }}
            options={typeOptions}
            placeholder={isLoadingTypes ? t('Ładowanie...') : t('Wybierz typ procesu')}
            disabled={isLoadingTypes}
            error={errors.type}
          />
          {errors.type && <ErrorMessage>{errors.type}</ErrorMessage>}
        </FormElement>

        {values.type === 'listener' && (
          <FormElement>
            <label>{t('Event domenowy')} *</label>
            <Select
              value={values.event_class}
              selectedOption={domainEventOptions.find(opt => opt.value === values.event_class) || null}
              onChange={(selected) => {
                setFieldValue('event_class', selected?.value || '');
              }}
              options={domainEventOptions}
              placeholder={isLoadingDomainEvents ? t('Ładowanie...') : t('Wyszukaj event domenowy...')}
              disabled={isLoadingDomainEvents}
              error={errors.event_class}
              isSearchable
            />
            {errors.event_class && <ErrorMessage>{errors.event_class}</ErrorMessage>}
          </FormElement>
        )}

        {values.type === 'policy' && (
          <FormElement>
            <label>{t('Rodzaj polityki')} *</label>
            <Select
              value={values.policy_kind}
              selectedOption={policyKindOptions.find(opt => opt.value === values.policy_kind) || null}
              onChange={(selected) => {
                const newKind = selected?.value || '';
                setFieldValue('policy_kind', newKind);
                setFieldValue('policy_subtype_class', '');
                setSelectedPolicyKind(newKind || null);
              }}
              options={policyKindOptions}
              placeholder={isLoadingPolicyKinds ? t('Ładowanie...') : t('Wybierz rodzaj polityki...')}
              disabled={isLoadingPolicyKinds}
              error={errors.policy_kind}
              isSearchable
            />
            {errors.policy_kind && <ErrorMessage>{errors.policy_kind}</ErrorMessage>}
          </FormElement>
        )}

        {values.type === 'policy' && !!values.policy_kind && (
          <FormElement>
            <label>{t('Podtyp polityki')}</label>
            <Select
              value={values.policy_subtype_class}
              selectedOption={policySubtypeOptions.find(opt => opt.value === values.policy_subtype_class) || null}
              onChange={(selected) => {
                setFieldValue('policy_subtype_class', selected?.value || '');
              }}
              options={policySubtypeOptions}
              placeholder={isLoadingPolicySubtypes ? t('Ładowanie...') : t('Wybierz podtyp polityki (opcjonalnie)...')}
              disabled={isLoadingPolicySubtypes}
              isSearchable
            />
          </FormElement>
        )}

        {values.type === 'additional_fields' && (
          <FormElement>
            <label>{t('Encja (serwis pól dodatkowych)')} *</label>
            <Select
              value={values.additional_fields_service}
              selectedOption={additionalFieldsServiceOptions.find(opt => opt.value === values.additional_fields_service) || null}
              onChange={(selected) => {
                setFieldValue('additional_fields_service', selected?.value || '');
              }}
              options={additionalFieldsServiceOptions}
              placeholder={isLoadingAdditionalFieldsServices ? t('Ładowanie...') : t('Wybierz encję...')}
              disabled={isLoadingAdditionalFieldsServices}
              error={errors.additional_fields_service}
              isSearchable
            />
            {errors.additional_fields_service && <ErrorMessage>{errors.additional_fields_service}</ErrorMessage>}
          </FormElement>
        )}

        {values.type === 'product_label' && (
          <FormElement>
            <label>{t('Pole dodatkowe (etykieta produktu)')} *</label>
            <Select
              value={values.product_label_field}
              selectedOption={productLabelFieldOptions.find(opt => opt.value === values.product_label_field) || null}
              onChange={(selected) => {
                setFieldValue('product_label_field', selected?.value || '');
              }}
              options={productLabelFieldOptions}
              placeholder={isLoadingProductLabelFields ? t('Ładowanie...') : t('Wybierz pole dodatkowe...')}
              disabled={isLoadingProductLabelFields}
              error={errors.product_label_field}
              isSearchable
            />
            {errors.product_label_field && <ErrorMessage>{errors.product_label_field}</ErrorMessage>}
          </FormElement>
        )}

        {values.type === 'product_label' && languagesData?.items?.map((lang) => (
          <FormElement key={lang.id}>
            <label>{t('Nazwa etykiety')} ({lang.name})</label>
            <Input
              type="text"
              value={values.translations[lang.id] || ''}
              onChange={(value) => setFieldValue(`translations.${lang.id}`, value)}
              placeholder={t('Np. Podwyższone koszty dostaw')}
            />
          </FormElement>
        ))}

        {values.type === 'provider' && (
          <FormElement>
            <label>{t('Rodzaj providera')} *</label>
            <Select
              value={values.provider_kind}
              selectedOption={providerKindOptions.find(opt => opt.value === values.provider_kind) || null}
              onChange={(selected) => {
                setFieldValue('provider_kind', selected?.value || '');
              }}
              options={providerKindOptions}
              placeholder={isLoadingProviderKinds ? t('Ładowanie...') : t('Wybierz rodzaj providera...')}
              disabled={isLoadingProviderKinds}
              error={errors.provider_kind}
              isSearchable
            />
            {errors.provider_kind && <ErrorMessage>{errors.provider_kind}</ErrorMessage>}
          </FormElement>
        )}

        {values.type === 'validator' && (
          <FormElement>
            <label>{t('Encja (klasa)')} *</label>
            <Select
              value={values.entity_class}
              selectedOption={entityClassOptions.find(opt => opt.value === values.entity_class) || null}
              onChange={(selected) => {
                setFieldValue('entity_class', selected?.value || '');
              }}
              options={entityClassOptions}
              placeholder={isLoadingEntityClasses ? t('Ładowanie...') : t('Wybierz encję...')}
              disabled={isLoadingEntityClasses}
              error={errors.entity_class}
              isSearchable
            />
            {errors.entity_class && <ErrorMessage>{errors.entity_class}</ErrorMessage>}
          </FormElement>
        )}

        <FormElement>
          <label>{t('Nazwa procesu')} *</label>
          <Input
            type="text"
            value={values.name}
            onChange={(value) => {
              setFieldValue('name', value);
            }}
            placeholder={t('Wpisz nazwę procesu')}
            error={errors.name}
          />
          {errors.name && <ErrorMessage>{errors.name}</ErrorMessage>}
        </FormElement>

        <FormElement>
          <label>{t('Opis')}</label>
          <textarea
            className={classnames(styles.textarea, { [styles.error]: !!errors.description })}
            value={values.description}
            onChange={(e) => {
              setFieldValue('description', e.target.value);
            }}
            placeholder={t('Wpisz opis procesu (opcjonalnie)')}
            rows={4}
          />
          {errors.description && <ErrorMessage>{errors.description}</ErrorMessage>}
        </FormElement>

        <div className={styles.actions}>
          <Button onClick={handleClose} color="gray" ghost>
            {t('Anuluj')}
          </Button>
          <Button onClick={() => handleSubmit()} color="primary">
            {t('Utwórz')}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default CreateProcessModal;
